import {FormControl, FormGroup} from '@angular/forms';

import {map} from 'rxjs/operators';
import {forkJoin, Subscription} from 'rxjs';

import {
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    FinancialQuestionnaireTypesEnum,
    IndividualsService,
    IndividualExtendedModel,
    FinancialQuestionnaireModel,
    IndividualAnnualNetIncomeInputModel,
    IndividualEstimatedNetWorthInputModel,
    IndividualFinancialQuestionnaireModel,
    ModifyIndividualFinancialQuestionnairesModel,
} from '@spa/api';

import {InArrayValidator} from '@spa/validators';

import {Helper, SelectControl} from '../../../../shared/classes';

import {ClientEditAmlEstimatedWorthFormErrorStatesEnum} from 'appEntries/panel/enums';

import {
    EstimatedWorthControls,
    AnnualNetIncomeSelector,
    EstimatedNetWorthSelector,
    AnnualNetIncomeCurrencySelector,
    IntendedInvestmentAmountSelector,
    EstimatedNetWorthCurrencySelector,
    AmountIntendedToInvestCurrencySelector,
} from '../../../../../assets/descriptors';

export class EstimatedWorthForm {
    private individual: IndividualExtendedModel;
    private financialLists: FinancialQuestionnaireModel[];

    private individualService: IndividualsService;

    public formGroup: FormGroup;
    public controls: SelectControl[];
    public currencyValueChangeSubscriptions: Subscription[];

    constructor(
        individual: IndividualExtendedModel,
        individualService: IndividualsService,
        financialList: FinancialQuestionnaireModel[]
    ) {
        this.individual = individual;
        this.individualService = individualService;
        this.financialLists = financialList;

        this.controls = [];
        this.currencyValueChangeSubscriptions = [];
    }

    private getControlValue(controlName: string | number): string | number {
        switch (controlName) {
            case AnnualNetIncomeSelector:
                return this.individual.annualNetIncomeId;
            case EstimatedNetWorthSelector:
                return this.individual.estimatedNetWorthId;
            case AnnualNetIncomeCurrencySelector:
            case EstimatedNetWorthCurrencySelector:
                return this.getAnnualNetAndNetWorthCurrencyValue(controlName);
            case IntendedInvestmentAmountSelector:
            case AmountIntendedToInvestCurrencySelector:
                return this.getIntendedAmountControlsValue(controlName);
        }
    }

    private getIndividualFinancialQuestionnaireByType(
        type: FinancialQuestionnaireTypesEnum
    ): IndividualFinancialQuestionnaireModel | undefined {
        return this.individual.individualFinancialQuestionnaires.find((question: IndividualFinancialQuestionnaireModel) => {
            return question.financialQuestionnaireType === type;
        });
    }

    private getAnnualNetAndNetWorthCurrencyValue(controlName: string): string | null {
        if (this.individual.individualFinancialQuestionnaires.length > 0) {
            const type = (controlName === AnnualNetIncomeCurrencySelector) ?
                FinancialQuestionnaireTypesEnum.AnnualNetIncome : FinancialQuestionnaireTypesEnum.EstimatedNetWorth;

            const filteredQuestionnaire = this.getIndividualFinancialQuestionnaireByType(type);

            if (filteredQuestionnaire) {
                return filteredQuestionnaire.getCurrency;
            }
        }

        return null;
    }

    private getIntendedAmountControlsValue(controlName: string): string | number |null {
        if (this.individual.individualFinancialQuestionnaires.length > 0) {
            const filteredQuestionnaire =
                this.getIndividualFinancialQuestionnaireByType(FinancialQuestionnaireTypesEnum.AmountIntendedToInvest);

            if (filteredQuestionnaire) {
                return (controlName === AmountIntendedToInvestCurrencySelector)
                    ? filteredQuestionnaire.getCurrency : filteredQuestionnaire.financialQuestionnaireId;
            }
        }

        return null;
    }

    private initControl(control: SelectControl): SelectControl {
        let type: string;

        switch (control.name) {
            case FinancialQuestionnaireTypesEnum.AnnualNetIncome:
                type = FinancialQuestionnaireTypesEnum.AnnualNetIncome;
                break;
            case FinancialQuestionnaireTypesEnum.EstimatedNetWorth:
                type = FinancialQuestionnaireTypesEnum.EstimatedNetWorth;
                break;
            case FinancialQuestionnaireTypesEnum.AmountIntendedToInvest:
                type = FinancialQuestionnaireTypesEnum.AmountIntendedToInvest;
                break;
        }

        const filteredFinancialQuestionnaireLists = this.financialLists.filter((financialItem: FinancialQuestionnaireModel) => {
            return financialItem.financialQuestionnaireType === type;
        });

        const financialQuestionnaireIds = this.financialLists.map((financialItem: FinancialQuestionnaireModel) => {
            return financialItem.id;
        });

        control.options = filteredFinancialQuestionnaireLists.map((financialList: FinancialQuestionnaireModel) => {
            return {
                label: financialList.description,
                value: financialList.id
            };
        });

        control.extendValidators(InArrayValidator(financialQuestionnaireIds));

        return control;
    }

    private initControlValueByCurrency(): void {
        const annualNetCurrencyControl = this.getControlByName(AnnualNetIncomeCurrencySelector);
        const estimatedNetCurrencyControl = this.getControlByName(EstimatedNetWorthCurrencySelector);
        const intendedAmountCurrencyControl = this.getControlByName(AmountIntendedToInvestCurrencySelector);

        if (annualNetCurrencyControl && annualNetCurrencyControl.value) {
            this.onCurrencyChange(
                AnnualNetIncomeSelector,
                annualNetCurrencyControl.value,
                FinancialQuestionnaireTypesEnum.AnnualNetIncome
            );
        }

        if (estimatedNetCurrencyControl && estimatedNetCurrencyControl.value) {
            this.onCurrencyChange(
                EstimatedNetWorthSelector,
                estimatedNetCurrencyControl.value,
                FinancialQuestionnaireTypesEnum.EstimatedNetWorth
            );
        }

        if (intendedAmountCurrencyControl && intendedAmountCurrencyControl.value) {
            this.onCurrencyChange(
                IntendedInvestmentAmountSelector,
                intendedAmountCurrencyControl.value,
                FinancialQuestionnaireTypesEnum.AmountIntendedToInvest
            );
        }
    }

    private getControlByName(name: string): SelectControl | undefined {
        return this.controls.find((control: SelectControl) => {
            return control.name === name;
        });
    }

    private refreshFormControlValueByName(controlName: string): void {
        const formControl = this.formGroup.get(controlName);

        if (formControl) {
            formControl.reset();
            formControl.updateValueAndValidity();
        }
    }

    private createAnnualNetIncomeUpdate(selectedAnnualIncome: number): ServerResponse<boolean> {
        const clonedNetIncome = this.individual.annualNetIncomeId;

        const model = new IndividualAnnualNetIncomeInputModel(
            this.individual.id,
            selectedAnnualIncome
        );

        return this.individualService.updateAnnualNetIncome(model).pipe(
            map((response: ServerResponseData<null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    this.individual.annualNetIncomeId = clonedNetIncome;

                    return Helper.errorsHelper().createErrorResponse<null>(
                        response,
                        ClientEditAmlEstimatedWorthFormErrorStatesEnum.UpdateAnnualNetIncomeFailed
                    );
                }

                this.individual.annualNetIncomeId = selectedAnnualIncome;

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    private updateEstimatedNetWorth(selectedEstimatedNetWorth: number): ServerResponse<boolean> {
        const clonedNetWorth = this.individual.estimatedNetWorthId;

        const model = new IndividualEstimatedNetWorthInputModel(
            this.individual.id,
            selectedEstimatedNetWorth
        );

        return this.individualService.updateEstimatedNetWorth(model).pipe(
            map((response: ServerResponseData<null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    this.individual.estimatedNetWorthId = clonedNetWorth;

                    return Helper.errorsHelper().createErrorResponse<null>(
                        response,
                        ClientEditAmlEstimatedWorthFormErrorStatesEnum.UpdateEstimatedNetWorthFailed
                    );
                }

                this.individual.estimatedNetWorthId = selectedEstimatedNetWorth;

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    private updateFinancialQuestionnaire(selectedAmountToInvest: number): ServerResponse<boolean> {
        const inputModel = new ModifyIndividualFinancialQuestionnairesModel(this.individual.id, [selectedAmountToInvest]);

        return this.individualService.updateIndividualFinancialQuestionnairesByIndividualId(inputModel).pipe(
            map((response: ServerResponseData<null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<null>(
                        response,
                        ClientEditAmlEstimatedWorthFormErrorStatesEnum.UpdateIntendedInvestAmountFailed
                    );
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    initForm(): void {
        this.formGroup = new FormGroup({});
        this.controls = [];

        const nonInitControlNames = [
            AnnualNetIncomeCurrencySelector,
            EstimatedNetWorthCurrencySelector,
            AmountIntendedToInvestCurrencySelector,
        ];

        const controls = EstimatedWorthControls().map((control: SelectControl) => {
            if (!nonInitControlNames.includes(control.name)) {
                control = this.initControl(control);
            }

            control.value = this.getControlValue(control.name);

            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });

        this.controls.push(...controls);

        this.initControlValueByCurrency();

        const annualNetIncomeCurrencySubscription = this.formGroup
            .get(AnnualNetIncomeCurrencySelector)
            .valueChanges
            .subscribe(() => this.refreshFormControlValueByName(AnnualNetIncomeSelector));

        const estimatedNetWorthCurrencySubscription = this.formGroup
            .get(EstimatedNetWorthCurrencySelector)
            .valueChanges
            .subscribe(() => this.refreshFormControlValueByName(EstimatedNetWorthSelector));

        const amountToInvestCurrencySubscription = this.formGroup
            .get(AmountIntendedToInvestCurrencySelector)
            .valueChanges
            .subscribe(() => this.refreshFormControlValueByName(IntendedInvestmentAmountSelector));

        this.currencyValueChangeSubscriptions.push(
            annualNetIncomeCurrencySubscription,
            estimatedNetWorthCurrencySubscription,
            amountToInvestCurrencySubscription
        );
    }

    onCurrencyChange(controlName: string, currency: string, questionnaireType: FinancialQuestionnaireTypesEnum): void {
        const filteredControl = this.controls.find((control: SelectControl) => control.name === controlName);

        if (filteredControl) {
            filteredControl.options = this.financialLists
                .filter((questionnaire: FinancialQuestionnaireModel) => {
                    const typeCheck = questionnaire.financialQuestionnaireType === questionnaireType;
                    const currencyCheck = questionnaire.currency === currency;

                    return typeCheck && currencyCheck && questionnaire.isActive;
                })
                .sort((current: FinancialQuestionnaireModel, next: FinancialQuestionnaireModel) => {
                    return current.order - next.order;
                })
                .map((questionnaire: FinancialQuestionnaireModel) => {
                    return {
                        label: questionnaire.description,
                        value: questionnaire.id
                    };
                });
        }
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const formControls = this.formGroup.controls;

        const statuses = Object.keys(formControls).map((key: string) => {
            if (formControls[key].invalid) {
                this.controls.map((formsControl: SelectControl) => {
                    if (formsControl.name === key) {
                        formsControl.errors = Object
                            .keys(formControls[key].errors)
                            .map((errorKey: string) => messageFunction(formsControl.placeholder)[errorKey]);
                    }
                });

                return false;
            }

            return true;
        });

        return statuses.indexOf(false) < 0;
    }

    public createRequest(): ServerResponse<boolean> | null {
        const requestStack = [];
        const selectedAnnualIncome = this.formGroup.value[AnnualNetIncomeSelector] as number;
        const selectedEstimatedNetWorth = this.formGroup.value[EstimatedNetWorthSelector] as number;
        const selectedAmountToInvest = this.formGroup.value[IntendedInvestmentAmountSelector] as number;

        const individualAmountToInvest =
            this.individual.individualFinancialQuestionnaires.find((item: IndividualFinancialQuestionnaireModel) => {
                return item.financialQuestionnaireType === FinancialQuestionnaireTypesEnum.AmountIntendedToInvest;
            });

        if (this.individual.annualNetIncomeId !== selectedAnnualIncome) {
            const updateRequest = this.createAnnualNetIncomeUpdate(selectedAnnualIncome);

            requestStack.push(updateRequest);
        }

        if (this.individual.estimatedNetWorthId !== selectedEstimatedNetWorth) {
            const updateRequest = this.updateEstimatedNetWorth(selectedEstimatedNetWorth);

            requestStack.push(updateRequest);
        }

        if (
            !individualAmountToInvest ||
            (individualAmountToInvest.financialQuestionnaireId !== selectedAmountToInvest)
        ) {
            const updateRequest = this.updateFinancialQuestionnaire(selectedAmountToInvest);

            requestStack.push(updateRequest);
        }

        if (requestStack.length !== 0) {
            return forkJoin(requestStack)
                .pipe(
                    map((responses: ServerResponseData<boolean>[]) => {
                        if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
                            return Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
                        }

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        return null;
    }
}
